<template>
  <el-dialog
    v-model="showAddDialog"
    width="900px"
    @close="handleClose"
    destroy-on-close append-to-body
    class="add-dialog-container"
  >
    <template #header>
      <div flex items-center text-size-16px fw-600>
        <el-icon size="20px" color="#0052D9"><WarningFilled /></el-icon>
        <span v-if="tableReservationInfo" ml-8px>{{ tableReservationInfo.isReserved ? "修改预订" : "选择桌台" }}</span>
        <span v-else ml-8px>新增预订</span>
      </div>
    </template>
    <div class="add-booking-content">
      <el-form
        ref="bookingFormRef"
        class="booking-editor-form"
        :model="bookingForm"
        :rules="rules"
        label-width="88px"
      >
        <div class="booking-editor-form__left">
          <el-form-item label="桌台：" prop="reserveTableIdList">
            <el-cascader
              v-model="bookingForm.reserveTableIdList"
              placeholder="请选择桌台"
              :options="tableCategoryList"
              :props="cascaderSetting"
              clearable
              collapse-tags-tooltip
              style="width: 100%"
            />
          </el-form-item>
          <div
            v-if="selectedReservationMember"
            class="reservation-member-card"
            data-test="reservation-selected-member"
          >
            <div class="reservation-member-card__content">
              <span class="reservation-member-card__badge">会员</span>
              <div>
                <div class="reservation-member-card__name">{{ selectedReservationMember.name || "会员" }}</div>
                <div class="reservation-member-card__phone">{{ selectedReservationMember.phone }}</div>
              </div>
            </div>
            <button
              type="button"
              class="reservation-member-card__remove"
              data-test="reservation-member-remove"
              @click="removeReservationMember"
            >
              删除
            </button>
          </div>
          <el-form-item label="预订人：" prop="booker">
            <el-input
              v-model="bookingForm.booker"
              :readonly="Boolean(selectedReservationMember)"
              placeholder="请输入预订人"
            >
              <template v-if="!selectedReservationMember" #suffix>
                <button
                  type="button"
                  class="reservation-member-entry"
                  data-test="reservation-member-entry"
                  @click="openReservationMemberSelector"
                >
                  会员
                </button>
              </template>
            </el-input>
          </el-form-item>
          <el-form-item label="联系电话：" prop="contact">
            <el-input
              v-model="bookingForm.contact"
              style="width: 100%"
              :readonly="Boolean(selectedReservationMember)"
              clearable
              placeholder="请输入联系电话（选填）"
            />
          </el-form-item>
          <el-form-item label="备注：" prop="theRemark">
            <el-input
              v-model="bookingForm.theRemark"
              type="textarea"
              :rows="4"
              maxlength="80"
              show-word-limit
              placeholder="请输入备注"
            />
          </el-form-item>
        </div>

        <div class="booking-editor-form__right">
          <el-form-item prop="startTime" label-width="0" class="reservation-time-form-item">
            <div class="reservation-time-picker">
              <div class="reservation-date-strip">
                <button
                  class="reservation-date-nav"
                  type="button"
                  :disabled="previousReservationDateDisabled"
                  aria-label="前一天"
                  @click="changeReservationDate(-1)"
                >
                  <el-icon><ArrowLeft /></el-icon>
                </button>
                <div class="reservation-date-list" aria-label="预订日期">
                  <button
                    v-for="dateOption in reservationDateOptions"
                    :key="dateOption.value"
                    class="reservation-date-option"
                    :class="{ active: isSelectedReservationDate(dateOption.value) }"
                    type="button"
                    :data-test="`reservation-date-option-${dateOption.value}`"
                    :aria-pressed="isSelectedReservationDate(dateOption.value)"
                    @click="selectReservationDate(dateOption.value)"
                  >
                    <span data-test="reservation-date-option" class="reservation-date-option__content">
                      <span class="reservation-date-option__week">{{ dateOption.weekLabel }}</span>
                      <span class="reservation-date-option__day">{{ dateOption.dayLabel }}</span>
                    </span>
                  </button>
                </div>
                <button
                  class="reservation-date-nav"
                  type="button"
                  :disabled="nextReservationDateDisabled"
                  aria-label="后一天"
                  @click="changeReservationDate(1)"
                >
                  <el-icon><ArrowRight /></el-icon>
                </button>
              </div>

              <div v-if="reservationTimeSlots.length" class="reservation-time-slots" aria-label="预订时间">
                <button
                  v-for="slot in reservationTimeSlots"
                  :key="slot.label"
                  class="reservation-time-slot"
                  :class="{ active: isSelectedReservationTime(slot.value) }"
                  type="button"
                  data-test="reservation-time-slot"
                  :aria-pressed="isSelectedReservationTime(slot.value)"
                  @click="selectReservationTime(slot.value)"
                >
                  {{ slot.label }}
                </button>
              </div>
              <div v-else class="reservation-time-empty">今日可选时间已结束，请选择后续日期</div>
            </div>
          </el-form-item>
        </div>
      </el-form>
    </div>
    <WorkerSelect
      :select-label="tableReservationInfo ? '操作员工' : '预订员工'"
      :selection-mode="tableReservationInfo ? 'default' : 'reservation'"
      :disabled="loading"
      :button-text="tableReservationInfo ? '确定修改' : '确定预订'"
      :secondary-button-text="!tableReservationInfo && canSendReservationSms ? '预订并发送短信' : undefined"
      @update:worker="handleUpdateWorker($event, 'reservation')"
      @secondary-click="handleUpdateWorker($event, 'reservation-and-sms')"
    />
    <MemberSelectorDialogV2
      v-if="memberSelectorVisible"
      v-model="memberSelectorVisible"
      selection-mode="reservation"
      @member-selected="handleReservationMemberSelected"
    />
  </el-dialog>
</template>

<script lang="ts" setup>
import type { Dayjs } from "dayjs"
import type { FormInstance, FormRules } from "element-plus"
import type { PropType } from "vue"
import type { ReservationSubmitIntent } from "./reservationSubmission"
import type {
  MerchantOmsCustomerReservation,
  MerchantOmsTableReservation,
  OldUnionReservation,
  TableItemType,
  TableReservationVO,
} from "@/api/business/types/table"
import type { SMSConfigTuple, SMSTypeInfoDTO } from "@/api/setting/type/setting"
import type { MerchantCustomerUserDetailDTO } from "@/api/user/types/member"
import { ArrowLeft, ArrowRight } from "@element-plus/icons-vue"
import dayjs from "dayjs"
import { ElMessage } from "element-plus"
import { computed, defineAsyncComponent, reactive, ref, watch } from "vue"
import { useRouter } from "vue-router"
import {
  getAvailableTableList,
  getTableCategoryListByShopId,
  updateTableReservation,
} from "@/api/business/table"
import { getMessageTypeList, getSMSConfig } from "@/api/setting"
import { getMemberInfoById } from "@/api/user/member"
import { useUserStore } from "@/store/modules/user"
import WorkerSelect from "../WorkerSelect/WorkerSelect.vue"
import {
  getReservationSmsContactError,
  validateOptionalReservationContact,
} from "./reservationContactValidation"
import {
  getReservationSubmitSuccessMessage,
  isReservationSmsAvailable,
  submitNewReservation,
} from "./reservationSubmission"

const MemberSelectorDialogV2 = defineAsyncComponent(() => import("@/components/MemberSelector/MemberSelectorDialogV2.vue"))

type UnionReservationExt = OldUnionReservation & {
  smartVersion?: MerchantOmsTableReservation["smartVersion"]
  reserveTableIdList?: number[] | number
  theRemark?: string
  customerReserveId?: MerchantOmsCustomerReservation["customerReserveId"]
  merchantUser?: string
  merchantUserContact?: string
}

interface ReservationDateOption {
  value: string
  weekLabel: string
  dayLabel: string
}

interface ReservationTimeSlot {
  label: string
  value: Date
}

const RESERVATION_DATE_OPTION_COUNT = 7
const RESERVATION_TIME_INTERVAL_MINUTES = 30
const RESERVATION_LAST_SLOT_HOUR = 23
const RESERVATION_LAST_SLOT_MINUTE = 30
const weekDayLabels = ["周日", "周一", "周二", "周三", "周四", "周五", "周六"]

const emit = defineEmits(["update:show", "refresh", "confirm"])
const props = defineProps({
  show: {
    type: Boolean,
    required: true,
  },
  tableReservationInfo: {
    type: Object as PropType<OldUnionReservation>,
    required: false,
  },
  defaultReserveTableIdList: {
    type: Array as PropType<number[]>,
    required: false,
  },
  defaultStartTime: {
    type: Date as PropType<Date>,
    required: false,
  },
})

const userStore = useUserStore()
const router = useRouter()
const isCentralCreate = computed(() => !props.tableReservationInfo)
const shopId = userStore.shopId
const showAddDialog = ref(false)
const loading = ref(false)
const memberSelectorVisible = ref(false)
const smsConfig = ref<SMSConfigTuple>()
const smsMessageTypes = ref<SMSTypeInfoDTO[]>([])
const bookingFormRef = ref<FormInstance>()
const tableCategoryList = ref<
  {
    value: number
    label: string
    children?: any[]
  }[]
>([])
const tableReservationInfo = ref(props.tableReservationInfo)

const cascaderSetting = {
  emitPath: false,
  multiple: !tableReservationInfo.value || Array.isArray(tableReservationInfo.value.reserveId),
  collapse: true,
}
const tableList = ref<TableItemType[]>([])

const getNextReservationHalfHour = (time: Dayjs) => {
  const rounded = time.second(0).millisecond(0)
  if (rounded.minute() < RESERVATION_LAST_SLOT_MINUTE) {
    return rounded.minute(RESERVATION_LAST_SLOT_MINUTE)
  }
  return rounded.add(1, "hour").minute(0)
}

const buildReservationTimeSlots = (selectedDate: Dayjs, now = dayjs()): ReservationTimeSlot[] => {
  const selectedDay = selectedDate.startOf("day")
  const end = selectedDay
    .hour(RESERVATION_LAST_SLOT_HOUR)
    .minute(RESERVATION_LAST_SLOT_MINUTE)
    .second(0)
    .millisecond(0)
  let cursor = selectedDay
  if (selectedDay.isSame(now, "day")) {
    cursor = getNextReservationHalfHour(now)
  }

  const slots: ReservationTimeSlot[] = []
  while (cursor.isSame(end) || cursor.isBefore(end)) {
    if (cursor.isAfter(now)) {
      slots.push({
        label: cursor.format("HH:mm"),
        value: cursor.toDate(),
      })
    }
    cursor = cursor.add(RESERVATION_TIME_INTERVAL_MINUTES, "minute")
  }
  return slots
}

const buildDefaultReservationStartTime = () => {
  const todaySlots = buildReservationTimeSlots(dayjs())
  if (todaySlots[0]) {
    return todaySlots[0].value
  }
  return dayjs().add(1, "day").startOf("day").toDate()
}

const getReservationDateOffset = (date: Dayjs) => {
  return date.startOf("day").diff(dayjs().startOf("day"), "day")
}

const getReservationDateWindowStartOffset = (date: Dayjs) => {
  const offset = getReservationDateOffset(date)
  if (offset <= 0) {
    return 0
  }
  return Math.floor(offset / RESERVATION_DATE_OPTION_COUNT) * RESERVATION_DATE_OPTION_COUNT
}

function buildDefaultBookingForm(): UnionReservationExt {
  const reserveId = tableReservationInfo.value?.reserveId
  const reserveTableId = tableReservationInfo.value?.reserveTableId
  if (tableReservationInfo.value) {
    const result: UnionReservationExt = {
      ...tableReservationInfo.value,
      startTime: new Date(tableReservationInfo.value.startTime),
      overtime: new Date(tableReservationInfo.value.overtime),
    }
    if ("remark" in tableReservationInfo.value) {
      result.theRemark = tableReservationInfo.value.remark
    }
    if ("bookerRemark" in tableReservationInfo.value) {
      result.theRemark = tableReservationInfo.value.bookerRemark
    }
    if (reserveId) {
      result.reserveId = reserveId
    }
    if (reserveTableId) {
      if (Array.isArray(reserveTableId)) {
        result.reserveTableIdList = reserveTableId.map((item) => item.tableId)
      } else if (cascaderSetting.multiple) {
        result.reserveTableIdList = [reserveTableId]
      } else {
        result.reserveTableIdList = reserveTableId
      }
    }
    return result
  } else {
    const startTime = props.defaultStartTime ? new Date(props.defaultStartTime) : buildDefaultReservationStartTime()
    const result: UnionReservationExt = {
      reserveTableIdList: props.defaultReserveTableIdList?.length ? [...props.defaultReserveTableIdList] : undefined,
      shopId,
      booker: "",
      contact: "",
      startTime,
      overtime: dayjs(startTime).add(10, "minutes").toDate(),
    }
    return result
  }
}

const bookingForm = reactive<UnionReservationExt>(buildDefaultBookingForm())
const selectedReservationMember = ref<MerchantCustomerUserDetailDTO>()
if (bookingForm.merchantUserId) {
  selectedReservationMember.value = {
    merchantCustomerUserId: bookingForm.merchantUserId,
    name: bookingForm.booker,
    phone: bookingForm.contact,
  } as MerchantCustomerUserDetailDTO
}
const canSendReservationSms = computed(() => isReservationSmsAvailable(
  smsConfig.value,
  smsMessageTypes.value,
  Boolean(selectedReservationMember.value),
))
const selectedReservationDate = ref(dayjs(bookingForm.startTime).startOf("day"))
const dateWindowStartOffset = ref(getReservationDateWindowStartOffset(selectedReservationDate.value))
const todayStart = computed(() => dayjs().startOf("day"))
const selectedReservationDateOffset = computed(() => selectedReservationDate.value.diff(todayStart.value, "day"))
const previousReservationDateDisabled = computed(() => selectedReservationDateOffset.value <= 0)
const nextReservationDateDisabled = computed(() => false)
const reservationDateOptions = computed<ReservationDateOption[]>(() =>
  Array.from({ length: RESERVATION_DATE_OPTION_COUNT }, (_, index) => {
    const offset = dateWindowStartOffset.value + index
    const date = todayStart.value.add(offset, "day")
    return {
      value: date.format("YYYY-MM-DD"),
      weekLabel: offset === 0 ? "今天" : offset === 1 ? "明天" : weekDayLabels[date.day()],
      dayLabel: date.format("MM-DD"),
    }
  }),
)
const reservationTimeSlots = computed(() => buildReservationTimeSlots(selectedReservationDate.value))

const isSelectedReservationDate = (dateValue: string) => {
  return selectedReservationDate.value.isSame(dayjs(dateValue), "day")
}

const isSelectedReservationTime = (slotValue: Date) => {
  return dayjs(bookingForm.startTime).isSame(slotValue, "minute")
}

const selectReservationTime = (slotValue: Date) => {
  bookingForm.startTime = new Date(slotValue)
  bookingFormRef.value?.clearValidate?.("startTime")
}

const selectReservationDate = (dateValue: string) => {
  selectedReservationDate.value = dayjs(dateValue).startOf("day")
  dateWindowStartOffset.value = getReservationDateWindowStartOffset(selectedReservationDate.value)
  const currentTimeLabel = dayjs(bookingForm.startTime).format("HH:mm")
  const slots = buildReservationTimeSlots(selectedReservationDate.value)
  const nextSlot = slots.find(slot => slot.label === currentTimeLabel) ?? slots[0]
  if (nextSlot) {
    selectReservationTime(nextSlot.value)
  }
}

const changeReservationDate = (offset: number) => {
  const nextDate = selectedReservationDate.value.add(offset, "day")
  if (nextDate.isBefore(todayStart.value, "day")) {
    return
  }
  if (nextDate.diff(todayStart.value, "day") >= RESERVATION_DATE_OPTION_COUNT) {
    return
  }
  selectReservationDate(nextDate.format("YYYY-MM-DD"))
}

const validateStartTime = (_rule: any, value: Date, callback: (_error?: string | Error) => void) => {
  if (!value) {
    callback(new Error("请选择预抵时间"))
    return
  }
  if (value.getTime() < new Date().getTime()) {
    callback(new Error("预抵时间必须晚于当前时间"))
  } else {
    callback()
  }
}

const validateOvertime = (_rule: any, value: Date, callback: (_error?: string | Error) => void) => {
  if (dayjs(value).isBefore(dayjs(bookingForm.startTime))) {
    callback(new Error("取消时间必须晚于预抵时间"))
  } else {
    callback()
  }
}

const rules = reactive<FormRules>({
  reserveTableIdList: [{ required: true, message: "桌台不能为空", trigger: "change" }],
  booker: [{ required: true, message: "请输入预订人", trigger: "change" }],
  contact: [
    { validator: validateOptionalReservationContact, trigger: "change" },
  ],
  startTime: [
    { required: true, message: "请选择预抵时间", trigger: "change" },
    { validator: validateStartTime, trigger: "change" },
  ],
  overtime: [
    { required: true, message: "请选择取消时间", trigger: "change" },
    { validator: validateOvertime, trigger: "change" },
  ],
})

watch(
  () => props.show,
  (val) => {
    showAddDialog.value = val
  },
  { immediate: true },
)

const getTableCategoryList = async () => {
  await getTableCategoryListByShopId(shopId).then((res) => {
    if (res.code === 0) {
      tableCategoryList.value = res.data.map((item) => {
        return {
          value: item.tableCategoryId as number,
          label: item.categoryName,
          children: [] as TableItemType[],
        }
      })
    } else {
      ElMessage.error(res.msg)
    }
  })
}

watch(
  () => bookingForm.startTime,
  (val) => {
    if (!val) {
      return
    }
    bookingForm.overtime = new Date(new Date(val).getTime() + 10 * 60 * 1000)
  },
  { immediate: true },
)

const openReservationMemberSelector = () => {
  memberSelectorVisible.value = true
}

const applyReservationMember = (member: MerchantCustomerUserDetailDTO) => {
  if (!member.merchantCustomerUserId) {
    ElMessage.error("会员信息缺少会员ID，请重新选择")
    return false
  }
  selectedReservationMember.value = member
  bookingForm.merchantUserId = member.merchantCustomerUserId
  bookingForm.booker = member.name || "会员"
  bookingForm.contact = member.phone || ""
  bookingFormRef.value?.clearValidate?.(["booker", "contact"])
  return true
}

const handleReservationMemberSelected = async (payload: number | MerchantCustomerUserDetailDTO) => {
  try {
    const member = typeof payload === "number"
      ? (await getMemberInfoById(payload)).data
      : payload
    if (applyReservationMember(member)) {
      memberSelectorVisible.value = false
    }
  } catch (_error) {
    ElMessage.error("会员信息获取失败，请重新选择")
  }
}

const removeReservationMember = () => {
  selectedReservationMember.value = undefined
  bookingForm.merchantUserId = undefined
  bookingForm.booker = ""
  bookingForm.contact = ""
  bookingFormRef.value?.clearValidate?.(["booker", "contact"])
}

const handleClose = () => {
  showAddDialog.value = false
  emit("update:show", false)
}

const handleUpdateWorker = (worker: string | number | undefined, intent: ReservationSubmitIntent) => {
  if (intent === "reservation-and-sms") {
    if (!canSendReservationSms.value) {
      ElMessage.warning("短信服务未开启、预订通知未开启或短信额度不足")
      return
    }
    const contactError = getReservationSmsContactError(bookingForm.contact)
    if (contactError) {
      ElMessage.error(contactError)
      return
    }
  }
  if (!bookingFormRef.value) {
    return
  }
  bookingFormRef.value?.validate((valid) => {
    if (valid) {
      loading.value = true
      createOrUpdateTableReservation(worker, intent)

      // getCurrentReservation(reserveTableId as number, bookingForm.startTime, bookingForm.reserveId as number)
      //   .then((res) => {
      //     if (res.code == 0) {
      //       if (res.data) {
      //         ElMessageBox({
      //           message: "该时间段前后4小时内存在其他预订，是否确定预订？",
      //           confirmButtonText: "确定",
      //           cancelButtonText: "暂不",
      //           showCancelButton: true,
      //           confirmButtonClass: "message-confirm-button",
      //           type: "warning"
      //         })
      //           .then(() => {
      //             createOrUpdateTableReservation(worker)
      //           })
      //           .catch(() => {
      //             loading.value = false
      //             return
      //           })
      //       } else {
      //         createOrUpdateTableReservation(worker)
      //       }
      //     }
      //   })
      //   .catch(() => {
      //     loading.value = false
      //   })
    }
  })
}

const createOrUpdateTableReservation = async (worker: string | number | undefined, intent: ReservationSubmitIntent) => {
  if (isCentralCreate.value) return openCentralCreate()
  const operator = worker == null ? undefined : String(worker)
  const reserveId = bookingForm.reserveId
  if (reserveId) {
    const { shopId, booker, contact, merchantUserId, startTime, overtime, theRemark, reserveTableIdList } = bookingForm
    const reserveTableId = reserveTableIdList
    if (Array.isArray(reserveTableId) || Array.isArray(reserveId)) {
      return
    }
    const body: MerchantOmsTableReservation = {
      reserveId,
      smartVersion: bookingForm.smartVersion,
      shopId,
      booker,
      contact,
      startTime,
      overtime,
      reserveTableId,
      updateBy: operator,
      remark: theRemark,
      merchantUserId,
    }
    await updateTableReservation(body)
      .then((res) => {
        if (res.code === 0) {
          ElMessage.success("修改预订成功")
          emit("update:show", false)
          emit("refresh")
          emit("confirm")
        } else {
          ElMessage.error(res.msg)
        }
      })
      .finally(() => {
        loading.value = false
      })
  } else {
    const selectedReservationEmployeeCorpUserId = worker == null ? undefined : Number(worker)
    const currentCorpUserId = userStore.getUserInfo()?.corpUserId
    const currentOperatorCorpUserId = currentCorpUserId == null ? undefined : String(currentCorpUserId)
    let resultReserveTableIds: number | number[]
    if (typeof bookingForm.reserveTableIdList === "number") {
      resultReserveTableIds = [bookingForm.reserveTableIdList]
    } else {
      resultReserveTableIds = bookingForm.reserveTableIdList || []
    }
    const params: TableReservationVO = {
      ...bookingForm,
      reserveTableIds: resultReserveTableIds,
      shopId,
      source: 0,
      status: 1,
      reservationEmployeeCorpUserId: selectedReservationEmployeeCorpUserId,
      createBy: currentOperatorCorpUserId,
      remark: bookingForm.theRemark,
    }

    // getTableInfo()
    // bookingForm.reserveTableId = bookingForm.reserveTableIdList?.map((item: any) => {
    //   let name = ""
    //   tableList.value.forEach((val) => {
    //     if (val.tableId == item[1]) {
    //       name = val.tableName
    //     }
    //   })

    //   return {
    //     tableId: item[1],
    //     tableName: name
    //   }
    // })

    await submitNewReservation(params, intent)
      .then((res) => {
        if (res.code == 0) {
          ElMessage.success(getReservationSubmitSuccessMessage(res))
          emit("update:show", false)
          emit("refresh")
          emit("confirm")
        } else {
          ElMessage.error(res.msg)
        }
      })
      .catch((error) => {
        console.error(error)
      })
      .finally(() => {
        loading.value = false
      })
  }
}

const fetchTableListById = async () => {
  await getAvailableTableList(shopId).then((res) => {
    if (res.code === 0) {
      tableList.value = res.data
    } else {
      ElMessage.error(res.msg)
    }
  })
}

const loadReservationSmsAvailability = async () => {
  try {
    const configResponse = await getSMSConfig()
    smsConfig.value = configResponse.code === 0 ? configResponse.data ?? undefined : undefined
    if (smsConfig.value?.status !== 1 || smsConfig.value.remainingSmsQuota <= 0) {
      return
    }

    const typeResponse = await getMessageTypeList()
    smsMessageTypes.value = typeResponse.code === 0 && Array.isArray(typeResponse.data)
      ? typeResponse.data
      : []
  } catch (error) {
    console.warn("获取预订短信可用性失败，已隐藏短信发送入口", error)
  }
}

function openCentralCreate() {
  emit("update:show", false)
  return router.push({ name: "ReservationWorkbench", query: {
    smartReservationPanel: "store",
    ...(props.defaultReserveTableIdList?.[0] ? { smartReservationTableId: String(props.defaultReserveTableIdList[0]) } : {}),
    ...(props.defaultStartTime ? { smartReservationDate: dayjs(props.defaultStartTime).format("YYYY-MM-DD") } : {}),
  } })
}

watch(() => props.show && isCentralCreate.value, async (active) => {
  if (active) await openCentralCreate()
}, { immediate: true })

if (!tableReservationInfo.value && !isCentralCreate.value) {
  loadReservationSmsAvailability()
}

Promise.all(isCentralCreate.value ? [] : [getTableCategoryList(), fetchTableListById()])
  .then(() => {
    // 生成可用桌台列表
    tableCategoryList.value.forEach((item) => {
      const children = tableList.value.filter((table) => table.tableCategoryId === item.value)
      item.children = children.map((table) => {
        return {
          value: table.tableId!,
          label: table.tableName,
        }
      })
    })

    // 过滤掉没有子分类的选项
    tableCategoryList.value = tableCategoryList.value.filter((item) =>
      item.children && item.children.length > 0,
    )
  })
  .catch((error) => {
    console.error(error)
  })

// 组合传给c端的桌台信息
// const getTableInfo = () => {
//   const tabelList: string[] = []
//   bookingForm.reserveTableIdList?.forEach((item: any) => {
//     const table = tableCategoryList.value.find((item2) => item2.value === item[0])
//     if (table) {
//       const tableType = table.label
//       const tableName = table.children?.find((item3) => item3.value === item[1]).label
//       const tableInfo = `${tableType}-${tableName}`
//       tabelList.push(tableInfo)
//     }
//   })
// }
</script>

<style lang="scss" scoped>
.add-dialog-container {
  :deep(.el-dialog__body) {
    padding-top: 8px;
  }
}

.add-booking-content {
  min-width: 0;
}

.booking-editor-form {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(360px, 1.08fr);
  gap: 28px;
  align-items: start;
}

.booking-editor-form__left,
.booking-editor-form__right {
  min-width: 0;
}

.booking-editor-form__left {
  :deep(.el-form-item:last-child) {
    margin-bottom: 0;
  }
}

.reservation-member-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 64px;
  padding: 10px 14px;
  margin: 0 0 18px 88px;
  background: var(--tea-color-primary-50);
  border: 1px solid var(--tea-color-primary);
  border-radius: 8px;
}

.reservation-member-card__content {
  display: flex;
  gap: 10px;
  align-items: center;
  min-width: 0;
}

.reservation-member-card__badge {
  flex: 0 0 auto;
  padding: 3px 8px;
  font-size: 12px;
  color: var(--tea-color-primary);
  background: var(--tea-color-bg-container, #fff);
  border-radius: 10px;
}

.reservation-member-card__name {
  font-size: 15px;
  font-weight: 600;
  color: var(--tea-color-text-primary);
}

.reservation-member-card__phone {
  margin-top: 3px;
  font-size: 13px;
  color: var(--tea-color-text-secondary);
}

.reservation-member-entry,
.reservation-member-card__remove {
  padding: 0;
  font: inherit;
  cursor: pointer;
  background: transparent;
  border: 0;
}

.reservation-member-entry {
  font-weight: 600;
  color: var(--tea-color-primary);
}

.reservation-member-card__remove {
  flex: 0 0 auto;
  color: var(--tea-color-danger);
}

.reservation-time-form-item {
  margin-bottom: 0;

  :deep(.el-form-item__content) {
    display: block;
    width: 100%;
  }
}

.reservation-time-picker {
  min-width: 0;
}

.reservation-date-strip {
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr) 32px;
  gap: 8px;
  align-items: center;
  margin-bottom: 14px;
}

.reservation-date-nav,
.reservation-date-option,
.reservation-time-slot {
  font: inherit;
  cursor: pointer;
  background: var(--tea-color-bg-surface);
  border: 1px solid var(--tea-color-border);
  transition:
    color var(--tea-motion-duration-fast) var(--tea-motion-easing-standard),
    background-color var(--tea-motion-duration-fast) var(--tea-motion-easing-standard),
    border-color var(--tea-motion-duration-fast) var(--tea-motion-easing-standard),
    box-shadow var(--tea-motion-duration-fast) var(--tea-motion-easing-standard);
}

.reservation-date-nav {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 40px;
  padding: 0;
  color: var(--tea-color-primary);
  border-radius: 6px;

  &:disabled {
    color: var(--tea-color-text-disabled);
    cursor: not-allowed;
    background: var(--tea-color-fill-disabled);
    border-color: var(--tea-color-border-subtle);
  }

  &:not(:disabled):hover {
    background: var(--tea-color-fill-subtle);
    border-color: var(--tea-color-primary);
  }
}

.reservation-date-list {
  display: flex;
  gap: 8px;
  min-width: 0;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.reservation-date-option {
  flex: 0 0 64px;
  min-width: 64px;
  height: 46px;
  padding: 5px 6px;
  color: var(--tea-color-text-secondary);
  border-radius: 6px;

  &:hover {
    color: var(--tea-color-primary);
    border-color: var(--tea-color-primary);
  }

  &.active {
    color: var(--tea-color-primary);
    background: var(--tea-color-primary-50);
    border-color: var(--tea-color-primary);
    box-shadow: var(--tea-shadow-focus);
  }
}

.reservation-date-option__content,
.reservation-date-option__week,
.reservation-date-option__day {
  display: block;
}

.reservation-date-option__content {
  line-height: 1.2;
}

.reservation-date-option__week {
  font-size: 13px;
  font-weight: 600;
}

.reservation-date-option__day {
  margin-top: 3px;
  font-size: 12px;
}

.reservation-time-slots {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  max-height: 232px;
  padding-right: 2px;
  overflow-y: auto;
}

.reservation-time-slot {
  min-width: 0;
  height: 38px;
  padding: 0 8px;
  font-size: 15px;
  font-weight: 500;
  color: var(--tea-color-text-regular);
  border-radius: 8px;

  &:hover {
    color: var(--tea-color-primary);
    background: var(--tea-color-fill-subtle);
    border-color: var(--tea-color-primary);
  }

  &.active {
    color: var(--tea-color-primary);
    background: var(--tea-color-primary-50);
    border-color: var(--tea-color-primary);
    box-shadow: var(--tea-shadow-focus);
  }
}

.reservation-time-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  padding: 16px;
  font-size: 14px;
  color: var(--tea-color-text-secondary);
  background: var(--tea-color-fill-subtle);
  border: 1px dashed var(--tea-color-border);
  border-radius: 8px;
}

@media (max-width: 760px) {
  .booking-editor-form {
    grid-template-columns: 1fr;
    gap: 18px;
  }

  .reservation-time-slots {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    max-height: 260px;
  }
}
</style>
